import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, MenuItem, IconButton, Switch,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, InputAdornment, CircularProgress, Alert, Tooltip,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Search as SearchIcon, ViewCarousel as ViewCarouselIcon,
} from '@mui/icons-material';
import api from '../../../services/api';

const TIPOS = [
  { value: 'promo', label: 'Promoção' },
  { value: 'noticia', label: 'Notícia' },
  { value: 'feature', label: 'Funcionalidade' },
  { value: 'parceiro', label: 'Parceiro' },
  { value: 'evento', label: 'Evento' },
  { value: 'merchant_cta', label: 'CTA Comerciantes' },
];

const ICONES = [
  'star', 'bolt', 'restaurant', 'shopping_bag', 'local_offer', 'event',
  'smartphone', 'wifi', 'water_drop', 'flight', 'shield', 'card_giftcard',
  'celebration', 'rocket_launch', 'local_fire_department', 'sports_soccer',
  'directions_bus', 'account_balance', 'favorite', 'notifications',
];

interface HomeCard {
  id: number;
  tipo: string;
  titulo: string;
  subtitulo: string | null;
  icone: string;
  cor_inicio: string;
  cor_fim: string;
  rota: string | null;
  url: string | null;
  posicao: number;
  activo: number;
  data_inicio: string | null;
  data_fim: string | null;
  updated_at: string;
}

const emptyForm = {
  tipo: 'promo',
  titulo: '',
  subtitulo: '',
  icone: 'star',
  cor_inicio: '#6233A0',
  cor_fim: '#422580',
  rota: '',
  url: '',
  posicao: 0,
  activo: true,
  data_inicio: '',
  data_fim: '',
};

const HomeCards: React.FC = () => {
  const [rows, setRows] = useState<HomeCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState<{ open: boolean; item: HomeCard | null }>({ open: false, item: null });
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/parametrizacoes/home-cards');
      setRows(res.data?.data || []);
    } catch { setError('Erro ao carregar cards'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm(emptyForm);
    setDialog({ open: true, item: null });
  };

  const openEdit = (item: HomeCard) => {
    setForm({
      tipo: item.tipo,
      titulo: item.titulo,
      subtitulo: item.subtitulo || '',
      icone: item.icone,
      cor_inicio: item.cor_inicio,
      cor_fim: item.cor_fim,
      rota: item.rota || '',
      url: item.url || '',
      posicao: item.posicao,
      activo: item.activo === 1,
      data_inicio: (item.data_inicio || '').slice(0, 10),
      data_fim: (item.data_fim || '').slice(0, 10),
    });
    setDialog({ open: true, item });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        subtitulo: form.subtitulo || null,
        rota: form.rota || null,
        url: form.url || null,
        data_inicio: form.data_inicio || null,
        data_fim: form.data_fim || null,
      };
      if (dialog.item) {
        await api.put(`/admin/parametrizacoes/home-cards/${dialog.item.id}`, payload);
      } else {
        await api.post('/admin/parametrizacoes/home-cards', payload);
      }
      setSuccess(dialog.item ? 'Card atualizado' : 'Card criado');
      setDialog({ open: false, item: null });
      load();
    } catch (e: any) {
      setError(e.response?.data?.error || 'Erro ao guardar');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id: number, titulo: string) => {
    if (!window.confirm(`Eliminar card "${titulo}"?`)) return;
    try {
      await api.delete(`/admin/parametrizacoes/home-cards/${id}`);
      setSuccess('Card eliminado');
      load();
    } catch { setError('Erro ao eliminar'); }
  };

  const filtered = rows.filter(r =>
    r.titulo.toLowerCase().includes(search.toLowerCase()) ||
    (r.subtitulo || '').toLowerCase().includes(search.toLowerCase())
  );

  const ColorPreview = ({ hex }: { hex: string }) => (
    <Box sx={{
      width: 20, height: 20, borderRadius: '4px',
      background: hex, border: '1px solid #ccc', display: 'inline-block', mr: 1, verticalAlign: 'middle',
    }} />
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ViewCarouselIcon color="primary" />
          <Box>
            <Typography variant="h5" fontWeight={700}>Cards da Home</Typography>
            <Typography variant="caption" color="text.secondary">
              Cartões dinâmicos exibidos na página inicial da app
            </Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Novo Card
        </Button>
      </Box>

      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>{success}</Alert>}

      <Alert severity="info" sx={{ mb: 3 }}>
        Cards activos aparecem na home da app ordenados por posição. Defina datas de início/fim para promoções temporárias — expiram automaticamente sem intervenção.
        <br />
        <strong>CTA Comerciantes</strong>: aparece ao lado da loja no Pagali Shop quando existe apenas uma loja activa. Use <em>Rota</em> (ex: <code>/become-merchant</code>) ou <em>URL</em> externa para o destino.
      </Alert>

      {/* Search */}
      <Box sx={{ mb: 2 }}>
        <TextField
          size="small" fullWidth
          placeholder="Pesquisar título ou subtítulo..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
      </Box>

      {/* Table */}
      <Paper variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={60}>Pos.</TableCell>
                <TableCell width={120}>Tipo</TableCell>
                <TableCell>Título / Subtítulo</TableCell>
                <TableCell width={120}>Cores</TableCell>
                <TableCell width={90}>Destino</TableCell>
                <TableCell width={80} align="center">Activo</TableCell>
                <TableCell width={100}>Validade</TableCell>
                <TableCell width={90} align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {rows.length === 0 ? 'Sem cards configurados' : 'Sem resultados'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map(row => (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>{row.posicao}</TableCell>
                    <TableCell>
                      <Chip size="small" label={TIPOS.find(t => t.value === row.tipo)?.label || row.tipo} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{row.titulo}</Typography>
                      {row.subtitulo && <Typography variant="caption" color="text.secondary">{row.subtitulo}</Typography>}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 16, height: 16, borderRadius: '3px', background: row.cor_inicio, border: '1px solid #ddd' }} />
                        <Box sx={{ width: 16, height: 16, borderRadius: '3px', background: row.cor_fim, border: '1px solid #ddd' }} />
                      </Box>
                    </TableCell>
                    <TableCell>
                      {row.rota ? (
                        <code style={{ fontSize: 11, background: 'rgba(127, 127, 127, 0.15)', padding: '2px 5px', borderRadius: 3 }}>{row.rota}</code>
                      ) : row.url ? (
                        <Typography variant="caption" color="primary">URL ext.</Typography>
                      ) : (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={row.activo ? 'Activo' : 'Inactivo'}
                        color={row.activo ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>
                      {row.data_inicio && <div>Início: {new Date(row.data_inicio).toLocaleDateString('pt-CV', { timeZone: 'UTC' })}</div>}
                      {row.data_fim && <div>Fim: {new Date(row.data_fim).toLocaleDateString('pt-CV', { timeZone: 'UTC' })}</div>}
                      {!row.data_inicio && !row.data_fim && '—'}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => openEdit(row)}><EditIcon fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton size="small" color="error" onClick={() => handleDelete(row.id, row.titulo)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Dialog */}
      <Dialog open={dialog.open} onClose={() => setDialog({ open: false, item: null })} maxWidth="sm" fullWidth>
        <DialogTitle>{dialog.item ? 'Editar Card' : 'Novo Card'}</DialogTitle>
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              select label="Tipo" value={form.tipo}
              onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))}
              size="small" sx={{ flex: 1 }}
            >
              {TIPOS.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </TextField>
            <TextField
              label="Posição" type="number" value={form.posicao}
              onChange={e => {
                const n = parseInt(e.target.value, 10);
                setForm(f => ({ ...f, posicao: isNaN(n) ? 0 : n }));
              }}
              size="small" sx={{ width: 100 }}
              helperText="0 = primeiro"
            />
          </Box>

          <TextField
            label="Título" value={form.titulo}
            onChange={e => setForm(f => ({ ...f, titulo: e.target.value }))}
            fullWidth size="small" required
            placeholder="ex: Recarga de Natal — 10% extra"
          />
          <TextField
            label="Subtítulo (opcional)" value={form.subtitulo}
            onChange={e => setForm(f => ({ ...f, subtitulo: e.target.value }))}
            fullWidth size="small"
            placeholder="ex: Válido até 31 de Dezembro"
          />

          <TextField
            select label="Ícone" value={form.icone}
            onChange={e => setForm(f => ({ ...f, icone: e.target.value }))}
            size="small" fullWidth
          >
            {ICONES.map(ic => <MenuItem key={ic} value={ic}>{ic}</MenuItem>)}
          </TextField>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Cor início (hex)</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ColorPreview hex={form.cor_inicio} />
                <TextField
                  size="small" value={form.cor_inicio}
                  onChange={e => setForm(f => ({ ...f, cor_inicio: e.target.value }))}
                  placeholder="#6233A0" sx={{ flex: 1 }}
                />
              </Box>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Cor fim (hex)</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ColorPreview hex={form.cor_fim} />
                <TextField
                  size="small" value={form.cor_fim}
                  onChange={e => setForm(f => ({ ...f, cor_fim: e.target.value }))}
                  placeholder="#422580" sx={{ flex: 1 }}
                />
              </Box>
            </Box>
          </Box>

          {/* Preview */}
          <Box sx={{
            background: `linear-gradient(135deg, ${form.cor_inicio}, ${form.cor_fim})`,
            borderRadius: 2, p: 2, color: 'white',
          }}>
            <Typography variant="subtitle1" fontWeight={700}>{form.titulo || 'Título do card'}</Typography>
            {form.subtitulo && <Typography variant="caption" sx={{ opacity: 0.8 }}>{form.subtitulo}</Typography>}
          </Box>

          <TextField
            label="Rota interna (ex: /food, /hub)" value={form.rota}
            onChange={e => setForm(f => ({ ...f, rota: e.target.value }))}
            fullWidth size="small"
            helperText="Rota Flutter (Get.toNamed). Deixe vazio se usar URL externa."
          />
          <TextField
            label="URL externa (ex: https://...)" value={form.url}
            onChange={e => setForm(f => ({ ...f, url: e.target.value }))}
            fullWidth size="small"
            helperText="Abre no browser. Tem prioridade sobre a rota interna."
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Data início" type="date" value={form.data_inicio}
              onChange={e => setForm(f => ({ ...f, data_inicio: e.target.value }))}
              size="small" sx={{ flex: 1 }}
              InputLabelProps={{ shrink: true }}
              helperText="Opcional"
            />
            <TextField
              label="Data fim" type="date" value={form.data_fim}
              onChange={e => setForm(f => ({ ...f, data_fim: e.target.value }))}
              size="small" sx={{ flex: 1 }}
              InputLabelProps={{ shrink: true }}
              helperText="Opcional"
            />
          </Box>

          <FormControlLabel
            control={<Switch checked={form.activo} onChange={e => setForm(f => ({ ...f, activo: e.target.checked }))} />}
            label="Card activo"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog({ open: false, item: null })}>Cancelar</Button>
          <Button
            variant="contained" onClick={handleSave}
            disabled={saving || !form.titulo.trim()}
          >
            {saving ? <CircularProgress size={18} /> : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HomeCards;
